import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { financialAPI, companyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import BackButton from '../components/BackButton';
import { DonutChart, BarChart, colorAt } from '../components/MiniCharts';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const CURRENT_YEAR  = new Date().getFullYear();
const CURRENT_MONTH = new Date().getMonth() + 1; // 1-12

const METRICS = {
  expenses: { label: 'Expenses',        accent: 'var(--color-danger)'  },
  income:   { label: 'Income',          accent: 'var(--color-success)' },
  balance:  { label: 'Account Balance', accent: 'var(--color-accent)'  },
};

export default function Charts() {
  const { metric: routeMetric } = useParams();
  const navigate = useNavigate();
  const { business, parentCompany, isOwner, isEnterprise } = useAuth();

  const metric = METRICS[routeMetric] ? routeMetric : 'expenses';
  const meta   = METRICS[metric];

  const [view,     setView]     = useState('month');   // 'month' | 'year'
  const [year,     setYear]     = useState(CURRENT_YEAR);
  const [month,    setMonth]    = useState(CURRENT_MONTH);
  const [category, setCategory] = useState('');        // year-view filter
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);

  // Enterprise multi-business switcher
  const multiBiz = isOwner && isEnterprise;
  const [businesses, setBusinesses] = useState([]);
  const [bizId,      setBizId]      = useState('');

  useEffect(() => {
    if (!multiBiz) return;
    companyAPI.listBiz()
      .then(r => {
        const list = r.data.businesses || [];
        setBusinesses(list);
        setBizId(prev => prev || business?.id || list[0]?.id || '');
      })
      .catch(() => {});
  }, [multiBiz, business?.id]);

  // Reset the category filter whenever the metric changes (categories differ).
  useEffect(() => { setCategory(''); }, [metric]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { metric, year };
    if (view === 'month')                                params.month    = month;
    if (view === 'year' && category && metric !== 'balance') params.category = category;

    const req = (multiBiz && bizId)
      ? companyAPI.getBizCharts(bizId, params)
      : financialAPI.charts(params);

    req.then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [metric, year, month, view, category, multiBiz, bizId]);

  useEffect(() => { load(); }, [load]);

  const cur = data?.currency || parentCompany?.currency || business?.currency || '';
  const fmt = n =>
    `${cur} ${new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(Number(n) || 0)}`;
  const fmtFull = n =>
    `${cur} ${new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0)}`;

  const breakdown = data?.breakdown || [];
  const monthly   = data?.monthly   || [];
  const total     = data?.total     || 0;
  const isBalance = metric === 'balance';
  const monthRow  = monthly.find(m => m.month === month);

  const periodLabel = view === 'month' ? `${MONTHS[month - 1]} ${year}` : `Jan – Dec ${year}`;

  return (
    <div className="page">
      <BackButton />

      <h1 className="page-title" style={{ marginBottom: '0.2rem' }}>Charts</h1>
      <p className="text-muted" style={{ fontSize: '0.8rem', marginBottom: '0.9rem' }}>
        Visual financial reports
      </p>

      {/* Business switcher — Enterprise owners with multiple businesses */}
      {multiBiz && businesses.length > 1 && (
        <select
          value={bizId}
          onChange={e => setBizId(e.target.value)}
          style={{ width: '100%', marginBottom: '0.9rem', fontSize: '0.88rem' }}
        >
          {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      )}

      {/* Metric tabs */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.8rem', flexWrap: 'wrap' }}>
        {Object.entries(METRICS).map(([key, m]) => (
          <button
            key={key}
            onClick={() => navigate(`/charts/${key}`)}
            style={{
              padding: '0.45rem 1rem', borderRadius: 999, border: 'none', cursor: 'pointer',
              fontSize: '0.82rem', fontWeight: 600,
              background: metric === key ? 'var(--color-accent)' : 'var(--color-surface)',
              color:      metric === key ? '#fff' : 'var(--color-muted)',
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Month / Year toggle */}
      <div style={{ display: 'inline-flex', background: 'var(--color-surface)', borderRadius: 999,
                    padding: 3, marginBottom: '1rem' }}>
        {['month', 'year'].map(v => (
          <button
            key={v}
            onClick={() => setView(v)}
            style={{
              padding: '0.35rem 1rem', borderRadius: 999, border: 'none', cursor: 'pointer',
              fontSize: '0.78rem', fontWeight: 600,
              background: view === v ? 'var(--color-surface2)' : 'transparent',
              color:      view === v ? 'var(--color-text)' : 'var(--color-muted)',
            }}
          >
            {v === 'month' ? 'Single month' : 'Over months'}
          </button>
        ))}
      </div>

      {/* Period navigator */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
        <button onClick={() => setYear(y => y - 1)} className="btn-ghost"
                style={{ padding: '0.2rem 0.6rem', borderRadius: 8, cursor: 'pointer' }}>‹</button>
        <div style={{ textAlign: 'center', minWidth: 130 }}>
          <div style={{ fontWeight: 700 }}>{year}</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--color-muted)' }}>{periodLabel}</div>
        </div>
        <button onClick={() => setYear(y => y + 1)} disabled={year >= CURRENT_YEAR} className="btn-ghost"
                style={{ padding: '0.2rem 0.6rem', borderRadius: 8, cursor: year >= CURRENT_YEAR ? 'not-allowed' : 'pointer',
                         opacity: year >= CURRENT_YEAR ? 0.4 : 1 }}>›</button>
      </div>

      {/* Month picker — single-month view only */}
      {view === 'month' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', justifyContent: 'center', marginBottom: '1rem' }}>
          {MONTHS.map((m, i) => (
            <button
              key={m}
              onClick={() => setMonth(i + 1)}
              style={{
                padding: '0.28rem 0.55rem', borderRadius: 7, border: 'none', cursor: 'pointer',
                fontSize: '0.72rem', fontWeight: 600,
                background: month === i + 1 ? 'var(--color-accent)' : 'var(--color-surface)',
                color:      month === i + 1 ? '#fff' : 'var(--color-muted)',
              }}
            >
              {m}
            </button>
          ))}
        </div>
      )}

      {/* Category / product filter — over-months view, not for balance */}
      {view === 'year' && !isBalance && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
          <select value={category} onChange={e => setCategory(e.target.value)} style={{ fontSize: '0.8rem', maxWidth: 220 }}>
            <option value="">All {metric === 'income' ? 'products' : 'categories'}</option>
            {(data?.categories || []).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}

      {loading ? (
        <p className="text-muted" style={{ textAlign: 'center', marginTop: '2rem' }}>Loading…</p>

      ) : isBalance ? (
        /* ── ACCOUNT BALANCE ── */
        view === 'month' ? (
          /* Single month — opening → closing waterfall card */
          <div className="card" style={{ padding: '0.4rem 1.1rem' }}>
            <p className="text-muted" style={{ fontSize: '0.72rem', margin: '0.6rem 0 0.2rem' }}>
              Operating cash · month-end balance · {periodLabel}
            </p>
            <BalRow label="Opening balance"        value={fmtFull(monthRow?.opening)} />
            <BalRow label="+ Cash from sales"      value={fmtFull(monthRow?.cashIn)}    color="var(--color-success)" />
            <BalRow label="− Purchase payments"    value={fmtFull(monthRow?.purchases)} color="var(--color-danger)" />
            <BalRow label="− Expenses paid"        value={fmtFull(monthRow?.expenses)}  color="var(--color-danger)" />
            <BalRow label="Closing balance"        value={fmtFull(monthRow?.total)}     bold
                    color={Number(monthRow?.total) < 0 ? 'var(--color-danger)' : 'var(--color-text)'} />
          </div>
        ) : (
          /* Over months — running balance bars */
          <div className="card" style={{ padding: '1.2rem 1.1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1rem' }}>
              <span style={{ fontSize: '0.78rem', color: 'var(--color-muted)' }}>Operating cash · month-end balance</span>
              <span style={{ fontWeight: 700, color: total < 0 ? 'var(--color-danger)' : 'var(--color-success)' }}>{fmt(total)}</span>
            </div>
            <BarChart
              bars={monthly.map(m => ({ label: MONTHS[m.month - 1], value: m.total }))}
              signColors
              formatMoney={fmt}
            />
          </div>
        )

      ) : total === 0 && view === 'month' ? (
        <div className="card" style={{ padding: '2rem 1rem', textAlign: 'center' }}>
          <p className="text-muted">No {meta.label.toLowerCase()} recorded for {periodLabel}.</p>
        </div>

      ) : view === 'month' ? (
        /* ── SINGLE MONTH — donut + itemized list (image 1 style) ── */
        <>
          <div className="card" style={{ padding: '1.2rem', display: 'flex', justifyContent: 'center', marginBottom: '0.9rem' }}>
            <DonutChart data={breakdown} centerTop={meta.label} centerMain={fmt(total)} />
          </div>

          <div className="card" style={{ padding: '0.4rem 1.1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.6rem 0',
                          borderBottom: '1px solid var(--color-border)', fontWeight: 700 }}>
              <span>{periodLabel}</span>
              <span>{fmtFull(total)}</span>
            </div>
            {breakdown.map((b, i) => (
              <div key={b.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                          padding: '0.55rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.88rem' }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: colorAt(i), flexShrink: 0 }} />
                  {b.label}
                </span>
                <span style={{ fontSize: '0.88rem', fontWeight: 600, color: meta.accent }}>{fmtFull(b.amount)}</span>
              </div>
            ))}
          </div>
        </>

      ) : (
        /* ── OVER MONTHS — bar chart (image 2 style) ── */
        <div className="card" style={{ padding: '1.2rem 1.1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1rem' }}>
            <span style={{ fontSize: '0.78rem', color: 'var(--color-muted)' }}>
              {category || `All ${metric === 'income' ? 'products' : 'categories'}`}
            </span>
            <span style={{ fontWeight: 700 }}>{fmt(monthly.reduce((s, m) => s + m.total, 0))}</span>
          </div>
          <BarChart
            bars={monthly.map(m => ({ label: MONTHS[m.month - 1], value: m.total }))}
            color={meta.accent}
            formatMoney={fmt}
          />
        </div>
      )}
    </div>
  );
}

function BalRow({ label, value, color, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '0.55rem 0', borderTop: bold ? '1px solid var(--color-border)' : 'none',
                  borderBottom: bold ? 'none' : '1px solid rgba(255,255,255,0.04)' }}>
      <span style={{ fontSize: '0.88rem', fontWeight: bold ? 700 : 400, color: bold ? 'inherit' : 'var(--color-muted)' }}>{label}</span>
      <span style={{ fontSize: '0.88rem', fontWeight: bold ? 700 : 600, color: color || 'inherit' }}>{value}</span>
    </div>
  );
}
